import './GenerateQuestionPrompt.scss';

import * as React from 'react';

import { translate } from '~l10n/client';
import { useAtom } from 'jotai';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { Link } from '~shared/components/Link';
import Textarea from '~shared/components/Textarea/Textarea';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { promptAtom } from '~teacher/components/QuizEditor/GenerateQuestionDialog/atom';
import { InformationIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/InformationIcon';

//TODO replace this with the actual URL when the article is finished.
const LEARN_MORE_PROMPT_URL =
  'https://help.socrative.com/en/articles/8732134-socrative-ai-question-generation#h_20a75b424f';

const MAX_PROMPT_LENGTH = 3000;

interface Props {
  suffix?: React.ReactNode;
}

export const GenerateQuestionPrompt = React.forwardRef(
  function GenerateQuestionPrompt(
    { suffix }: Props,
    ref: React.Ref<HTMLTextAreaElement>
  ) {
    const [prompt, setPrompt] = useAtom(promptAtom);
    const promptLength = prompt.length;

    return (
      <div className="grid auto-rows-min gap-2 generate-question-prompt-grid">
        <div className="grid grid-cols-2 items-end">
          <label
            className="text-base text-sans text-blue-900 leading-8"
            htmlFor="prompt"
          >
            {translate('Prompt')}{' '}
            <TooltipV2
              maxContentWidth={440}
              label={translate(
                'Concisely state the topic or subject with keywords like assessment type or standard to get questions that are relevant to your needs. You may also provide example questions to increase relevancy.'
              )}
            >
              <InformationIcon />
            </TooltipV2>
          </label>
          <div className="flex justify-end">
            {promptLength} / {MAX_PROMPT_LENGTH}
          </div>
        </div>
        <Textarea
          aria-describedby="prompt-description"
          id="prompt"
          ref={ref}
          maxLength={MAX_PROMPT_LENGTH}
          className="flex-grow row-start-2"
          placeholder={translate(
            'e.g., The solar system and natural satellites'
          )}
          maxRows={7}
          value={prompt}
          onChange={(event: any) => setPrompt(event.target.value)}
        />
        <div className="flex flex-shrink row-start-2 col-start-2">{suffix}</div>
        <div
          id="prompt-description"
          className="font-sans text-xs text-blue-600 items-start col-span-2 row-start-3"
        >
          {translate(
            'Write a prompt that will generate high quality questions.'
          )}{' '}
          <Link
            href={LEARN_MORE_PROMPT_URL}
            target="_blank"
            rel="noopener noreferrer"
            includeCommonStyles
            className="font-800 text-cyan-700 hocus:text-cyan-600"
            onClick={() => {
              new FeatureUseEventAdapter({
                feature: 'prompt_learn_more_link',
                source: 'generate_questions_step_1',
                action: 'click',
              }).sendAnalytics();
            }}
          >
            {translate('Learn More')}
          </Link>
        </div>
      </div>
    );
  }
);
